// PreRoundGamesPanel — stage side games BEFORE Start Round is tapped.
//
// Sits inside PlayPage. The panel owns nothing that touches the DB;
// parent-owned staged-games state flows in via `stagedGames` /
// `onChange`. PlayPage's commitRound (after init_standalone_round
// succeeds) walks the staged list and creates each game via
// /.netlify/functions/standalone-game.
//
// Defaults per Jacob's direction (2026-07-14):
//   * Games staged with ALL group players as participants — the common
//     case for casual rounds. Per-participant customization stays on
//     the in-round StandaloneGamesPanel via the existing "add / remove
//     participant" affordance.
//   * Teamed formats (Nassau, Match Play) auto-split by slot:
//     odds vs evens for Nassau; slot 0 vs slot 1 for Match Play (which
//     requires exactly 2 sides — additional players go to whichever
//     side has the fewer). Users can retune on the scorecard.
//   * Wolf rotation defaults to 'scorecard' (order of the players list
//     in activeRound.payload). The order selector surfaces in staging too
//     so a user isn't left wondering whether they were ever prompted (R2
//     of the sidegames-ux task).
//
// The panel does NOT gate on Pro tier. Casual-round (standalone) side games
// are FREE — the Pro gate only applies to side games created inside an
// event or league (enforced server-side in event-admin.js, which 402s a
// non-Pro create). There is no "can't stack two games" gate anywhere;
// stacking multiple casual games is allowed. Keeping the pre-round UI
// ungated matches the in-round StandaloneGamesPanel, which is also free.

import { useState } from 'react'
import GameInfoModal from './GameInfoModal'
import { validateSideGameSelection, preRoundAutoTeamCounts } from '../lib/sideGameValidation'
import { isUnder18 } from '../lib/ageGate'

const FORMAT_CATALOG = [
  { value: 'skins',      label: 'Skins',       min: 2, teamed: false, wolf: false, unitLabel: '$/skin' },
  { value: 'nassau',     label: 'Nassau',      min: 2, teamed: true,  wolf: false, unitLabel: '$/segment' },
  { value: 'stableford', label: 'Stableford',  min: 2, teamed: false, wolf: false, unitLabel: '$/round' },
  { value: 'simple',     label: 'Simple',      min: 2, teamed: false, wolf: false, unitLabel: '$/hole per opponent' },
  { value: 'match_1v1',  label: 'Match Play',  min: 2, teamed: true,  wolf: false, unitLabel: '$/match' },
  { value: 'wolf',       label: 'Wolf',        min: 3, teamed: false, wolf: true,  unitLabel: '$/point' },
  { value: 'vegas',      label: 'Vegas',       min: 4, max: 4, teamed: true,  wolf: false, vegas: true, unitLabel: '$/point' },
  { value: 'shuffle_card', label: 'Shuffle Card', min: 2, teamed: false, wolf: false, unitLabel: null, pro: true, shuffle: true, wagerless: true },
]

export default function PreRoundGamesPanel({
  stagedGames,
  onChange,
  playerCount,
  // Ordered seat labels (seat 0 = you, then each added player) so the Vegas
  // team picker can show a per-seat A/B assignment before the round starts
  // (player_ids don't exist yet — they're minted at commitRound, so the picker
  // works on SEAT INDEX and toCreateGamePayload maps seat → id).
  seatLabels = [],
  // Seat-indexed birthdate strings (YYYY-MM-DD) or nulls. As of the
  // F-100-T1 review fix, the picker's linked-friend rows CARRY birthdate
  // through search_players → PlayerSearchInput.pickPlayer → PlayPage's
  // roster (as linked_birthdate) → this prop, so the pre-flight
  // KNOWN-under-18 rejection catches BOTH seat 0 (the logged-in user)
  // AND linked friends — the case that motivated the fix. Stubs typed
  // by name/phone (no linked_player_id, so no linked_birthdate on the
  // roster row) still arrive as null here; the 18+ ack checkbox
  // (acceptance 2) and the server-side gate at netlify/functions/
  // standalone-game.js cover those. F-100-T1 acceptance 1.
  seatBirthdates = [],
  // Holes in this round (18 or 9) — drives the switch-6 vs switch-3 label.
  holeCount = 18,
}) {
  const switchBlock = holeCount <= 9 ? 3 : 6
  // Compact / expanded — collapse by default; a section with 0 games
  // rendered at 100px of space steals a lot of thumbtrail on iPhone SE.
  const [expanded, setExpanded] = useState(false)
  // Which format's info popup is open — feeds into GameInfoModal. null = closed.
  const [infoFormat, setInfoFormat] = useState(null)
  // F-100-T1 acceptance 1: transient error surfaced when a wager add is
  // refused because a KNOWN-under-18 birthdate participant is on the round.
  // Rendered inline above the "Add another" grid so the block is visible
  // BEFORE Start Round is tapped (mirrors StandaloneGamesPanel's toast copy).
  const [stagingError, setStagingError] = useState(null)

  // Pre-flight helper: walks the current group's seat-indexed birthdates
  // and returns the first KNOWN-under-18 seat (name + index). Nulls / missing
  // birthdates (linked friends, anonymous stubs) skip this check — the
  // stub-age ack checkbox + server gate cover those.
  const findKnownUnder18Seat = () => {
    for (let i = 0; i < playerCount; i++) {
      const bd = seatBirthdates?.[i]
      if (bd && isUnder18(bd)) {
        return { seat: i, name: seatLabels?.[i] || `Player ${i + 1}` }
      }
    }
    return null
  }

  // F-100-T1: whether a staged game is a MONEY game right now (matches the
  // isWagerEnabled server helper's shape for skins/nassau/match_1v1/others).
  // Drives both the ack-checkbox render and the canStart-gating consumed
  // by PlayPage via the exported isStagedGameStartable helper.
  const stagedGameWagerEnabled = (sg) => {
    const cat = FORMAT_CATALOG.find(f => f.value === sg.format)
    if (!cat || cat.wagerless) return false
    if (sg.format === 'nassau') {
      const b = sg.config?.bet_units || {}
      return (Number(b.front) || 0) > 0
        || (Number(b.back) || 0) > 0
        || (Number(b.overall) || 0) > 0
    }
    if (sg.format === 'match_1v1') {
      return (Number(sg.config?.bet_units) || 0) > 0
    }
    return (Number(sg.config?.unit_value) || 0) > 0
  }

  const addGame = (format) => {
    const cat = FORMAT_CATALOG.find(f => f.value === format)
    if (!cat) return
    // F-100-T1 acceptance 1: wager formats REFUSE to stage when a
    // KNOWN-under-18 birthdate participant is on the round. Server-side
    // gate at netlify/functions/standalone-game.js:190 would 403 later
    // with under_18_wager_block; this pre-flight surfaces the block
    // IMMEDIATELY so the user isn't left staring at a silently-missing
    // game after Start Round. Wagerless formats (Shuffle Card) skip
    // this — the mechanic isn't a money game.
    if (!cat.wagerless) {
      const minor = findKnownUnder18Seat()
      if (minor) {
        setStagingError(`cannot add ${minor.name} to a wager game — under 18`)
        return
      }
    }
    setStagingError(null)
    // Sensible defaults matching StandaloneGamesPanel's create form so
    // the DB row shape is identical whether staged pre-round or added
    // in-round. shuffle_mode (Shuffle Card) picks 'straight_up' as the
    // MVP default; the game creator can flip it via a chip on the
    // ShuffleCard component.
    let config
    switch (format) {
      case 'skins':
        config = { scoring_mode: 'gross', carry_from_ties: true, unit_value: 1 }
        break
      case 'nassau':
        config = { bet_units: { front: 1, back: 1, overall: 1 } }
        break
      case 'stableford':
      case 'simple':
        config = { unit_value: 1 }
        break
      case 'match_1v1':
        config = { bet_units: 1 }
        break
      case 'wolf':
        // Rotation defaults to scorecard (seat order). The picker below
        // surfaces the choice at staging so a user isn't left wondering
        // whether they were ever asked (R2 of sidegames-ux).
        // rotation_order_seats (seat-index array) is CLIENT-ONLY — stripped
        // in toCreateGamePayload and replaced with the real player_id array.
        config = {
          unit_value: 1,
          rotation_mode: 'scorecard',
          rotation_order_seats: Array.from({ length: playerCount }, (_, i) => i),
          blind_wolf_enabled: true,
        }
        break
      case 'vegas':
        // Two 2-player teams; flip defaults ON. teams_mode 'same' → the seat
        // picker below (default seats 0,1=A / 2,3=B; user can change or
        // Randomize) drives the split in toCreateGamePayload. teams_mode
        // 'switch6' → server auto-randomizes a rotation, no picker. ties_mode
        // 'wash' (current behavior) / 'push'. allow_press only in 'same'.
        config = {
          unit_value: 1,
          flip_enabled: true,
          scoring_mode: 'gross',
          teams_mode: 'same',
          ties_mode: 'wash',
          allow_press: false,
          team_seats: ['A', 'A', 'B', 'B'],
          // Intended round length so switch-6 blocks size to the round (three
          // 6-hole blocks on 18, three 3-hole blocks on 9) rather than the
          // course size. Read server-side by resolveVegasSwitchHoles.
          holes_intended: holeCount <= 9 ? 9 : 18,
        }
        break
      case 'shuffle_card':
        // Shuffle Card is wagerless — the mechanic is the game. Groups that
        // want money on the round stack Skins or Nassau on top. Config
        // omits unit_value entirely so the standalone-game backend won't
        // pick it up for settlement, and the UI hides the unit input.
        config = {
          shuffle_mode: 'straight_up',
          index_eligible_opt_in: false,
        }
        break
      default:
        config = { unit_value: 1 }
    }
    onChange([
      ...stagedGames,
      {
        tempId: `t_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
        format,
        config,
      },
    ])
  }

  const removeGame = (tempId) => {
    onChange(stagedGames.filter(g => g.tempId !== tempId))
  }

  // Patch a staged game's config in place.
  const patchConfig = (tempId, patch) => {
    onChange(stagedGames.map(sg =>
      sg.tempId === tempId ? { ...sg, config: { ...sg.config, ...patch } } : sg
    ))
  }
  // Assign a seat (0..3) to Team A or B for a staged Vegas game.
  const setSeatTeam = (tempId, seatIdx, team) => {
    onChange(stagedGames.map(sg => {
      if (sg.tempId !== tempId) return sg
      const seats = [...(sg.config?.team_seats || ['A', 'A', 'B', 'B'])]
      seats[seatIdx] = team
      return { ...sg, config: { ...sg.config, team_seats: seats } }
    }))
  }
  // Randomize the 4 seats into a fresh 2v2 split.
  const randomizeSeats = (tempId) => {
    const order = [0, 1, 2, 3]
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[order[i], order[j]] = [order[j], order[i]]
    }
    const seats = ['A', 'A', 'B', 'B']
    const next = ['A', 'A', 'B', 'B']
    order.forEach((seatIdx, k) => { next[seatIdx] = seats[k] })
    patchConfig(tempId, { team_seats: next })
  }

  // Swap two seats in the Wolf staged rotation-order-seats array.
  const moveWolfSeat = (tempId, from, to) => {
    onChange(stagedGames.map(sg => {
      if (sg.tempId !== tempId) return sg
      const seats = Array.isArray(sg.config?.rotation_order_seats)
        ? [...sg.config.rotation_order_seats]
        : Array.from({ length: playerCount }, (_, i) => i)
      if (to < 0 || to >= seats.length || from < 0 || from >= seats.length) return sg
      ;[seats[from], seats[to]] = [seats[to], seats[from]]
      return { ...sg, config: { ...sg.config, rotation_order_seats: seats } }
    }))
  }
  // Reset the Wolf staged rotation to natural seat order (used when
  // rotation_mode flips back to 'scorecard').
  const resetWolfSeats = (tempId) => {
    patchConfig(tempId, {
      rotation_order_seats: Array.from({ length: playerCount }, (_, i) => i),
    })
  }
  // Randomize the Wolf staged rotation (used when 'auto' is chosen at
  // staging so the user sees the order that will land).
  const randomizeWolfSeats = (tempId) => {
    const seats = Array.from({ length: playerCount }, (_, i) => i)
    for (let i = seats.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[seats[i], seats[j]] = [seats[j], seats[i]]
    }
    patchConfig(tempId, { rotation_order_seats: seats })
  }

  const updateUnit = (tempId, unit) => {
    const num = Number(unit)
    const safe = Number.isFinite(num) ? num : 0
    onChange(stagedGames.map(g => {
      if (g.tempId !== tempId) return g
      // Nassau's config nests three sub-units; every other format keeps
      // unit_value at the top. Preserve the shape the server expects.
      if (g.format === 'nassau') {
        return { ...g, config: { ...g.config, bet_units: { front: safe, back: safe, overall: safe } } }
      }
      if (g.format === 'match_1v1') {
        return { ...g, config: { ...g.config, bet_units: safe } }
      }
      return { ...g, config: { ...g.config, unit_value: safe } }
    }))
  }

  // Compute the inline requirement message for a staged game (R1a).
  // Pre-round the whole group is the selection, so selectedCount = playerCount.
  const validationFor = (sg) => {
    const cat = FORMAT_CATALOG.find(f => f.value === sg.format)
    if (!cat) return { valid: true }
    let teamCounts = { a: 0, b: 0 }
    if (cat.vegas) {
      const seats = Array.isArray(sg.config?.team_seats) ? sg.config.team_seats : []
      teamCounts = {
        a: seats.filter(t => t === 'A').length,
        b: seats.filter(t => t === 'B').length,
      }
    } else if (cat.teamed) {
      // Nassau / Match Play — the panel provides NO A/B UI here;
      // toCreateGamePayload auto-splits by seat (odds vs evens). Feed the
      // validator the same split so it doesn't fabricate an "Assign every
      // player to Team A or Team B" error the user has no way to clear
      // (reviewer finding, 2026-07-20).
      teamCounts = preRoundAutoTeamCounts(playerCount)
    }
    return validateSideGameSelection({
      format: sg.format,
      selectedCount: playerCount,
      teamCounts,
      vegasTeamsMode: sg.config?.teams_mode === 'switch6' ? 'switch6' : 'same',
      wolfRotationMode: sg.config?.rotation_mode || 'scorecard',
      // Wolf staged order-length matches playerCount by default; if the
      // implementer ever adds a shorter set-mode UI, validation will trip.
      wolfOrderLen: Array.isArray(sg.config?.rotation_order_seats)
        ? sg.config.rotation_order_seats.length
        : playerCount,
    })
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        onClick={() => setExpanded(x => !x)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 12px',
          background: 'var(--green-deep)',
          border: '1px solid var(--green-mid)',
          borderRadius: 'var(--radius)',
          cursor: 'pointer',
        }}
      >
        <div>
          <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Side Games <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span>
          </p>
          <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--gray-500)' }}>
            {stagedGames.length === 0
              ? 'Add Skins, Nassau, Wolf, Shuffle Card, and more.'
              : `${stagedGames.length} staged — start together with your round`}
          </p>
        </div>
        <span style={{ fontSize: '1.2rem', color: 'var(--gray-300)', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}>
          ›
        </span>
      </div>

      {expanded && (
        <div style={{
          marginTop: 8, padding: 10,
          border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
          background: 'rgba(0,0,0,0.15)',
        }}>
          {/* Staged games list */}
          {stagedGames.length > 0 && (
            <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
              {stagedGames.map(g => {
                const cat = FORMAT_CATALOG.find(f => f.value === g.format)
                if (!cat) return null
                const unit = g.format === 'nassau'
                  ? (g.config?.bet_units?.overall ?? 0)
                  : g.format === 'match_1v1'
                    ? (g.config?.bet_units ?? 0)
                    : (g.config?.unit_value ?? 0)
                const validation = validationFor(g)
                return (
                  <div key={g.tempId} style={{
                    padding: '10px 10px 8px',
                    borderRadius: 6,
                    background: 'var(--green-dark)',
                    border: '1px solid var(--green-mid)',
                    display: 'grid', gap: 8,
                  }}>
                    {/* R4: header row — name (flex:1), unit input, remove ✕.
                        Anchored at the top of the card so unit/remove stay
                        with the game name instead of floating mid-list.  */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{cat.label}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>
                          All {playerCount} players
                          {cat.teamed && !cat.vegas && playerCount > 2 && ' · auto-split by seat'}
                          {cat.wolf && ' · wolf rotates hole by hole'}
                          {cat.vegas && ' · 2 teams of 2'}
                        </div>
                      </div>
                      {!cat.wagerless && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <input
                            type="number" min={0} step="0.25"
                            value={unit}
                            onChange={(e) => updateUnit(g.tempId, e.target.value)}
                            aria-label={`${cat.label} ${cat.unitLabel || 'unit'}`}
                            style={{
                              width: 60, padding: '4px 6px', borderRadius: 4,
                              background: 'var(--green-deep)', color: 'var(--cream)',
                              border: '1px solid var(--green-mid)',
                              fontSize: '0.8rem', textAlign: 'right',
                            }}
                          />
                          <span style={{ fontSize: '0.65rem', color: 'var(--gray-500)', minWidth: 32 }}>
                            {cat.unitLabel?.split(' ')[0]}
                          </span>
                        </div>
                      )}
                      {cat.wagerless && (
                        <span style={{ fontSize: '0.65rem', color: 'var(--gray-500)', flexShrink: 0 }}>
                          no wager
                        </span>
                      )}
                      <button
                        onClick={() => removeGame(g.tempId)}
                        aria-label={`Remove ${cat.label}`}
                        style={{
                          width: 28, height: 28, borderRadius: 4,
                          border: '1px solid var(--gray-500)',
                          background: 'transparent', color: 'var(--gray-300)',
                          fontSize: '1rem', cursor: 'pointer', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >×</button>
                    </div>

                    {/* Vegas config — teams mode / seat picker / ties / press / flip */}
                    {cat.vegas && (() => {
                      const teamsMode = g.config?.teams_mode === 'switch6' ? 'switch6' : 'same'
                      const tiesMode = g.config?.ties_mode === 'push' ? 'push' : 'wash'
                      const seats = g.config?.team_seats || ['A', 'A', 'B', 'B']
                      const aCount = seats.filter(t => t === 'A').length
                      const bCount = seats.filter(t => t === 'B').length
                      return (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {/* Teams mode */}
                          <div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', marginBottom: 3 }}>Teams</div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {[
                                { v: 'same', label: 'Same all round' },
                                { v: 'switch6', label: `Switch every ${switchBlock}` },
                              ].map(m => (
                                <button
                                  key={m.v} type="button"
                                  onClick={() => patchConfig(g.tempId, m.v === 'switch6' ? { teams_mode: 'switch6', allow_press: false } : { teams_mode: 'same' })}
                                  style={chip(teamsMode === m.v)}
                                >{m.label}</button>
                              ))}
                            </div>
                          </div>

                          {/* Team picker (same mode only) — R4: hint + Randomize on
                              a single aligned row above the seat list. */}
                          {teamsMode === 'same' && (
                            <div>
                              <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                gap: 8, marginBottom: 4,
                              }}>
                                <span style={{ fontSize: '0.68rem', color: 'var(--gray-300)' }}>
                                  Assign 2 to A and 2 to B
                                </span>
                                <button
                                  type="button"
                                  onClick={() => randomizeSeats(g.tempId)}
                                  style={{ ...chip(false), flex: 'none', padding: '3px 10px', fontSize: '0.62rem' }}
                                >🎲 Randomize</button>
                              </div>
                              <div style={{ display: 'grid', gap: 3 }}>
                                {[0, 1, 2, 3].map(seatIdx => (
                                  <div key={seatIdx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ flex: 1, fontSize: '0.72rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {seatLabels[seatIdx] || `Player ${seatIdx + 1}`}
                                    </span>
                                    {['A', 'B'].map(t => (
                                      <button
                                        key={t} type="button"
                                        onClick={() => setSeatTeam(g.tempId, seatIdx, t)}
                                        style={{
                                          padding: '2px 8px', borderRadius: 4,
                                          border: `1px solid ${seats[seatIdx] === t ? 'var(--gold)' : 'var(--green-mid)'}`,
                                          background: seats[seatIdx] === t ? 'var(--gold)' : 'transparent',
                                          color: seats[seatIdx] === t ? 'var(--green-deep)' : 'var(--cream)',
                                          fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                                        }}
                                      >{t}</button>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {teamsMode === 'switch6' && (
                            <p style={{
                              margin: 0, fontSize: '0.68rem', color: 'var(--gray-300)',
                              padding: '6px 8px', borderRadius: 4,
                              background: 'rgba(212,185,122,0.08)', border: '1px solid var(--green-mid)',
                            }}>
                              Teams rotate automatically every {switchBlock} holes — no assignment needed.
                            </p>
                          )}

                          {/* Ties mode */}
                          <div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', marginBottom: 3 }}>Ties</div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {[
                                { v: 'wash', label: 'Wash' },
                                { v: 'push', label: 'Push (carries & doubles next hole)' },
                              ].map(m => (
                                <button
                                  key={m.v} type="button"
                                  onClick={() => patchConfig(g.tempId, { ties_mode: m.v })}
                                  style={chip(tiesMode === m.v)}
                                >{m.label}</button>
                              ))}
                            </div>
                          </div>

                          {/* Allow press (same mode only) */}
                          {teamsMode === 'same' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: 'var(--gray-300)', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={g.config?.allow_press === true}
                                onChange={(e) => patchConfig(g.tempId, { allow_press: e.target.checked })}
                              />
                              Allow Press — losing team can double a hole
                            </label>
                          )}

                          {/* Flip */}
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: 'var(--gray-300)', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={g.config?.flip_enabled !== false}
                              onChange={(e) => patchConfig(g.tempId, { flip_enabled: e.target.checked })}
                            />
                            Flip on natural birdie (eagle doubles)
                          </label>
                        </div>
                      )
                    })()}

                    {/* R2: Wolf rotation staging — same three options the in-round
                        panel offers. When 'set' the seat-labeled order editor
                        surfaces; 'auto' randomizes on staging so the user sees
                        the order that will land. */}
                    {cat.wolf && (() => {
                      const mode = g.config?.rotation_mode || 'scorecard'
                      const seats = Array.isArray(g.config?.rotation_order_seats)
                        ? g.config.rotation_order_seats.filter(s => s < playerCount)
                        : Array.from({ length: playerCount }, (_, i) => i)
                      return (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>
                            Rotation order (who's Wolf on hole 1, hole 2, and so on)
                          </div>
                          <div style={{ display: 'grid', gap: 3 }}>
                            {[
                              { v: 'scorecard', label: 'Use scorecard order' },
                              { v: 'auto',      label: 'Choose for me (random)' },
                              { v: 'set',       label: 'Set the order myself' },
                            ].map(o => (
                              <label
                                key={o.v}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', cursor: 'pointer' }}
                              >
                                <input
                                  type="radio"
                                  checked={mode === o.v}
                                  onChange={() => {
                                    patchConfig(g.tempId, { rotation_mode: o.v })
                                    if (o.v === 'scorecard') resetWolfSeats(g.tempId)
                                    else if (o.v === 'auto') randomizeWolfSeats(g.tempId)
                                  }}
                                />
                                {o.label}
                              </label>
                            ))}
                          </div>
                          {(mode === 'set' || mode === 'auto') && seats.length > 0 && (
                            <div>
                              <div style={{ fontSize: '0.62rem', color: 'var(--gray-500)', marginBottom: 3 }}>
                                {mode === 'set' ? 'Tap ↑↓ to reorder:' : 'Order the server will use:'}
                              </div>
                              <div style={{ display: 'grid', gap: 3 }}>
                                {seats.map((seatIdx, i) => (
                                  <div key={`${seatIdx}-${i}`} style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '4px 8px', borderRadius: 4,
                                    background: 'var(--green-deep)', border: '1px solid var(--green-mid)',
                                    fontSize: '0.75rem',
                                  }}>
                                    <span style={{ color: 'var(--gray-400)', fontSize: '0.65rem', width: 16 }}>{i + 1}.</span>
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {seatLabels[seatIdx] || `Player ${seatIdx + 1}`}
                                    </span>
                                    {mode === 'set' && (
                                      <>
                                        <button
                                          type="button"
                                          disabled={i === 0}
                                          onClick={() => moveWolfSeat(g.tempId, i, i - 1)}
                                          aria-label="Move up"
                                          style={miniBtn}
                                        >↑</button>
                                        <button
                                          type="button"
                                          disabled={i === seats.length - 1}
                                          onClick={() => moveWolfSeat(g.tempId, i, i + 1)}
                                          aria-label="Move down"
                                          style={miniBtn}
                                        >↓</button>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })()}

                    {/* Shuffle mode */}
                    {cat.shuffle && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[
                          { v: 'straight_up',   label: 'Straight Up' },
                          { v: 'double_down',   label: 'Double Down' },
                          { v: 'duel',          label: 'Duel' },
                        ].map(m => {
                          const active = (g.config?.shuffle_mode || 'straight_up') === m.v
                          return (
                            <button
                              key={m.v}
                              onClick={() => patchConfig(g.tempId, { shuffle_mode: m.v })}
                              type="button"
                              style={{
                                padding: '3px 8px', borderRadius: 4,
                                border: `1px solid ${active ? 'var(--gold)' : 'var(--green-mid)'}`,
                                background: active ? 'rgba(212,185,122,0.15)' : 'transparent',
                                color: active ? 'var(--gold)' : 'var(--gray-300)',
                                fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                              }}
                            >{m.label}</button>
                          )
                        })}
                      </div>
                    )}

                    {/* R1a: inline requirement line — always visible when the
                        staged game is not startable, in plain language, so a
                        broken card can't slip past a silent-disabled Start. */}
                    {!validation.valid && (
                      <p
                        role="status"
                        style={{
                          margin: 0, padding: '6px 8px', borderRadius: 4,
                          background: 'rgba(229,115,115,0.12)',
                          border: '1px solid var(--red, #e57373)',
                          color: 'var(--red, #e57373)',
                          fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.35,
                        }}
                      >{validation.reason}</p>
                    )}

                    {/* F-100-T1 acceptance 2: 18+ ack checkbox for wager-
                        enabled staged games. Mirrors StandaloneGamesPanel's
                        create-form checkbox (src/components/StandaloneGamesPanel
                        .jsx:804) so the copy + shape stay consistent across
                        the three wager-create surfaces (F-094 standalone,
                        F-101 edit, F-100 pre-round). PlayPage's canStart
                        gates on stub_age_ack being present for any wager
                        staged game (see isStagedGameStartable export).
                        Wagerless (Shuffle Card) and $0 bragging games skip
                        the ack — the mechanic isn't a money game. */}
                    {stagedGameWagerEnabled(g) && (
                      <label
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8,
                          padding: '8px 10px', borderRadius: 6,
                          background: 'rgba(212,185,122,0.08)',
                          border: '1px solid var(--gold)',
                          fontSize: '0.75rem', cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={g.config?.stub_age_ack === true}
                          onChange={(e) => patchConfig(
                            g.tempId,
                            { stub_age_ack: e.target.checked ? true : undefined },
                          )}
                          style={{ marginTop: 3 }}
                        />
                        <span>
                          I confirm every participant in this wager is 18 or older.
                          <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.68rem', marginTop: 2 }}>
                            Required for money games (TournyFlex never holds funds).
                          </span>
                        </span>
                      </label>
                    )}
                    {stagedGameWagerEnabled(g) && g.config?.stub_age_ack !== true && (
                      <p
                        role="status"
                        style={{
                          margin: 0, padding: '6px 8px', borderRadius: 4,
                          background: 'rgba(229,115,115,0.12)',
                          border: '1px solid var(--red, #e57373)',
                          color: 'var(--red, #e57373)',
                          fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.35,
                        }}
                      >Confirm every participant is 18+ before starting this wager.</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* F-100-T1 acceptance 1: pre-flight rejection banner for a wager
              add attempted while a KNOWN-under-18 birthdate participant is
              on the round. Cleared on the next successful add. */}
          {stagingError && (
            <p
              role="status"
              style={{
                margin: '0 0 8px', padding: '8px 10px', borderRadius: 4,
                background: 'rgba(229,115,115,0.12)',
                border: '1px solid var(--red, #e57373)',
                color: 'var(--red, #e57373)',
                fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.35,
              }}
            >{stagingError}</p>
          )}

          {/* Add-game grid — R5 adds a small ⓘ button next to each format
              tile so a curious user can read the "How to play" summary
              without adding the game first. */}
          <p style={{ fontSize: '0.72rem', color: 'var(--gray-300)', margin: '0 0 4px' }}>
            {stagedGames.length === 0 ? 'Tap a game to add:' : 'Add another:'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 6 }}>
            {FORMAT_CATALOG.map(f => {
              // Vegas is exactly 4 (2v2). A 5-player casual group must not
              // stage it — toCreateGamePayload would ship all 5 and the
              // server rejects 'wrong_size'. `max` gates the upper bound.
              const tooFew = playerCount < f.min
              const tooMany = f.max != null && playerCount > f.max
              const disabled = tooFew || tooMany
              const disabledReason = tooFew
                ? `Needs ${f.min}+ players`
                : tooMany
                  ? `Needs exactly ${f.max} players`
                  : ''
              const alreadyStaged = stagedGames.some(g => g.format === f.value)
              return (
                <div key={f.value} style={{ position: 'relative' }}>
                  <button
                    onClick={() => !disabled && addGame(f.value)}
                    disabled={disabled}
                    title={disabled ? disabledReason : `Add ${f.label}`}
                    style={{
                      width: '100%',
                      padding: '8px 6px', borderRadius: 6,
                      border: `1px solid ${alreadyStaged ? 'var(--gold)' : 'var(--green-mid)'}`,
                      background: alreadyStaged ? 'rgba(212, 185, 122, 0.15)' : 'var(--green-dark)',
                      color: disabled ? 'var(--gray-500)' : 'var(--cream)',
                      fontSize: '0.75rem', fontWeight: 600,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                      position: 'relative',
                    }}
                  >
                    <span>{f.label}</span>
                    {f.pro && (
                      <span style={{
                        fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.05em',
                        color: 'var(--gold)', textTransform: 'uppercase',
                      }}>
                        Pro
                      </span>
                    )}
                    {alreadyStaged && (
                      <span style={{ fontSize: '0.6rem', color: 'var(--gold)' }}>staged</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setInfoFormat(f.value) }}
                    aria-label={`How ${f.label} works`}
                    title={`How ${f.label} works`}
                    style={{
                      position: 'absolute', top: 2, right: 2,
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'transparent', border: 'none',
                      color: alreadyStaged ? 'var(--gold)' : 'var(--gray-400)',
                      fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                      lineHeight: 1, padding: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >ⓘ</button>
                </div>
              )
            })}
          </div>

          <p style={{ fontSize: '0.65rem', color: 'var(--gray-500)', margin: '10px 0 0', lineHeight: 1.4 }}>
            Money amounts are display-only. TournyFlex never handles wager funds.
            All group players are added by default — customize teams and
            participants on the scorecard after the round starts.
          </p>
        </div>
      )}

      <GameInfoModal format={infoFormat} onClose={() => setInfoFormat(null)} />
    </div>
  )
}

// Small pill-toggle style for the Vegas mode selectors.
function chip(active) {
  return {
    flex: 1, padding: '4px 6px', borderRadius: 4,
    border: `1px solid ${active ? 'var(--gold)' : 'var(--green-mid)'}`,
    background: active ? 'rgba(212,185,122,0.15)' : 'transparent',
    color: active ? 'var(--gold)' : 'var(--gray-300)',
    fontSize: '0.62rem', fontWeight: 600, cursor: 'pointer', lineHeight: 1.2,
  }
}

const miniBtn = {
  padding: '2px 8px', borderRadius: 4,
  background: 'transparent', color: 'var(--cream)',
  border: '1px solid var(--green-mid)',
  fontSize: '0.7rem', cursor: 'pointer', minWidth: 26,
}

// F-100-T1: mirrors stagedGameWagerEnabled inside the component (kept as a
// standalone export so PlayPage.canStart can gate Start Round on the ack
// without re-importing FORMAT_CATALOG or duplicating the shape check).
// Returns true when the staged game is a MONEY game right now.
export function stagedGameIsWagerEnabled(sg) {
  if (!sg || !sg.format) return false
  if (sg.format === 'shuffle_card') return false
  if (sg.format === 'nassau') {
    const b = sg.config?.bet_units || {}
    return (Number(b.front) || 0) > 0
      || (Number(b.back) || 0) > 0
      || (Number(b.overall) || 0) > 0
  }
  if (sg.format === 'match_1v1') {
    return (Number(sg.config?.bet_units) || 0) > 0
  }
  return (Number(sg.config?.unit_value) || 0) > 0
}

// F-100-T1: whether the staged game's stub-age ack is satisfied. A
// wagerless / $0 bragging game is trivially satisfied; a money game
// requires the checkbox ticked (config.stub_age_ack === true).
export function stagedGameAckOk(sg) {
  if (!stagedGameIsWagerEnabled(sg)) return true
  return sg?.config?.stub_age_ack === true
}

// Helper for PlayPage.commitRound — turns a staged game entry into a
// create_game request payload. `orderedPlayerIds` is the group's player
// IDs in seat order. `format`-specific logic:
//   - Vegas: teams_mode 'same' → team_id from the per-seat picker (team_seats);
//            'switch6' → team_id null (server builds + seeds the rotation).
//   - Other teamed formats: odds vs evens (Nassau) or 0-vs-1 (match_1v1).
//   - Wolf: rotation_order = orderedPlayerIds, remapped through the
//     rotation_order_seats picker; unknown/removed seats fall back to natural
//     order.
//   - Others: participants without team_id.
export function toCreateGamePayload(staged, orderedPlayerIds) {
  const { format, config } = staged
  const cat = FORMAT_CATALOG.find(f => f.value === format)
  if (!cat) return null
  const ids = orderedPlayerIds.filter(Boolean)
  if (ids.length < cat.min) return null
  // Upper bound (Vegas = exactly 4). Guards the case where the group grew
  // past `max` after the game was staged — shipping >max would 'wrong_size'
  // server-side; returning null skips it cleanly instead.
  if (cat.max != null && ids.length > cat.max) return null

  let participants
  let finalConfig = { ...config }

  if (cat.vegas) {
    const teamsMode = config?.teams_mode === 'switch6' ? 'switch6' : 'same'
    if (teamsMode === 'switch6') {
      // Server builds the rotation + seeds team_ids from the participant order.
      participants = ids.map(pid => ({ player_id: pid, team_id: null }))
    } else {
      const seats = config?.team_seats || ['A', 'A', 'B', 'B']
      const a = seats.filter(t => t === 'A').length
      const b = seats.filter(t => t === 'B').length
      if (a !== 2 || b !== 2) return null // invalid 2v2 — skip rather than 500
      participants = ids.map((pid, i) => ({ player_id: pid, team_id: seats[i] === 'B' ? 'B' : 'A' }))
    }
    // Strip the client-only seat map before sending.
    const { team_seats, ...rest } = finalConfig
    finalConfig = rest
  } else if (cat.teamed) {
    // For >2 participants, split by seat: seat 0 = A, seat 1 = B,
    // then alternate. Match_1v1 with 3+ people ends up as 2 vs 1;
    // organizer can retune. For exactly 2 the split is trivial.
    participants = ids.map((pid, i) => ({
      player_id: pid,
      team_id: i % 2 === 0 ? 'A' : 'B',
    }))
  } else {
    participants = ids.map(pid => ({ player_id: pid, team_id: null }))
  }

  if (cat.wolf) {
    // Map seat-indexed staged rotation → real player_ids. Fall back to
    // natural id order for any seat outside the current group (e.g. the
    // group shrank after staging), and de-dupe.
    const seats = Array.isArray(config?.rotation_order_seats)
      ? config.rotation_order_seats.filter(s => Number.isInteger(s) && s >= 0 && s < ids.length)
      : ids.map((_, i) => i)
    const seen = new Set()
    const ordered = []
    for (const s of seats) {
      const pid = ids[s]
      if (pid && !seen.has(pid)) { seen.add(pid); ordered.push(pid) }
    }
    for (const pid of ids) {
      if (!seen.has(pid)) { seen.add(pid); ordered.push(pid) }
    }
    // Never ship the client-only seat array; the server only knows about
    // rotation_order (player_ids) + rotation_mode.
    const { rotation_order_seats, ...rest } = finalConfig
    finalConfig = { ...rest, rotation_order: ordered }
    // The server's 'auto' branch shuffles ONLY when the supplied
    // rotation_order is invalid (missing / wrong length). We just supplied a
    // valid one, so 'auto' would degrade to seat-order silently. Downgrade
    // rotation_mode to 'set' here to keep the staged shuffle authoritative.
    if (finalConfig.rotation_mode === 'auto') {
      finalConfig.rotation_mode = 'set'
    }
  }
  return { format, config: finalConfig, participants }
}
